import { gqlClient } from '@/api/graphql-client';
import timelogKeys from '@/api/queries/timelog/keyFactor';
import { graphql } from '@/gql';
import {
  GetTimelogsInput,
  GetTimelogsQuery,
  PaginationInput,
} from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import { useQuery } from '@tanstack/react-query';

const GET_TIMELOGS = graphql(`
  query GetTimelogs($input: GetTimelogsInput, $pagination: PaginationInput!) {
    timelogs(input: $input, pagination: $pagination) {
      limit
      offset
      totalCount
      totalDuration
      totalCost {
        value
        currency {
          id
          code
          exponent
        }
      }
      items {
        id
        endTime
        startTime
        type {
          id
          name
        }
        user {
          id
          fullName
          firstName
          lastName
          email
          colour
          avatarUrl
          costPerHour {
            value
            currency {
              id
              code
              exponent
            }
          }
        }
        duration
        costPerHour {
          value
          currency {
            id
            code
            exponent
          }
        }
        cost {
          value
          currency {
            id
            code
            exponent
          }
        }
        notes
        linkedEntities {
          entityId
          entityType
          entityReadableIdentifier
        }
        primaryLinkedEntity {
          entityId
          entityType
          entityReadableIdentifier
        }
      }
    }
  }
`);

export type Timelog = GetTimelogsQuery['timelogs']['items'][0];

/**
 * True when `input` carries a present-but-empty `assignees` list, i.e. a filter
 * is active that resolves to no one (e.g. a team with no active members). The
 * API treats an empty list as "no filter", so callers should skip the request
 * and show nothing instead. An absent list (`undefined`) means "no filter".
 */
export const filterMatchesNoOne = (input?: GetTimelogsInput): boolean =>
  Array.isArray(input?.assignees) && input.assignees.length === 0;

export const useGetTimelogs = (
  input?: GetTimelogsInput,
  pagination?: PaginationInput
) => {
  const paginationInput = pagination || PAGINATION_ARG_DEFAULTS;

  return useQuery({
    queryKey: timelogKeys.list(input, paginationInput),
    enabled: !filterMatchesNoOne(input),
    queryFn: () => {
      return gqlClient.request(GET_TIMELOGS, {
        input,
        pagination: paginationInput,
      });
    },
  });
};
